/**
 * First-try Recipes / Stock / pars / invoices / receipting / Subscriptions /
 * Food Safety: one job on the page. Hide a different feature’s Home card so
 * Guided Success (or the kitchen day) is the first screen — not a leftover
 * stock tip above invoices / Food Safety. Purchasing Orders can still show a tip.
 *
 * Catalogue is one job only on the recipes / par product-gate handoff
 * (`?from=recipes` or `?from=par`). Nav → Catalogue can still show a Home tip.
 */

import { shouldHidePrimaryNudgeOnCheckoutFlow } from './growthEngineOrderSuccess'
import { normalizeGrowthEnginePath } from './growthEngineNudgeRefresh'

export const GROWTH_ENGINE_CATALOGUE_HANDOFF_PARAM = 'from'
export const GROWTH_ENGINE_CATALOGUE_HANDOFF_RECIPES = 'recipes'
export const GROWTH_ENGINE_CATALOGUE_HANDOFF_PAR = 'par'

const CATALOGUE_PRODUCT_GATE_FROM = new Set([
  GROWTH_ENGINE_CATALOGUE_HANDOFF_RECIPES,
  GROWTH_ENGINE_CATALOGUE_HANDOFF_PAR,
])

export function isGrowthEngineRecipesFeaturePath(pathname) {
  return /(?:^|\/)menu-builder$/.test(normalizeGrowthEnginePath(pathname))
}

export function isGrowthEngineStockFeaturePath(pathname) {
  return /(?:^|\/)stockTakes$/.test(normalizeGrowthEnginePath(pathname))
}

export function isGrowthEngineParFeaturePath(pathname) {
  return /(?:^|\/)products$/.test(normalizeGrowthEnginePath(pathname))
}

export function isGrowthEngineInvoicesFeaturePath(pathname) {
  return /(?:^|\/)purchasing\/invoices$/.test(
    normalizeGrowthEnginePath(pathname),
  )
}

export function isGrowthEngineReceiptingFeaturePath(pathname) {
  return /(?:^|\/)purchasing\/receipting$/.test(
    normalizeGrowthEnginePath(pathname),
  )
}

export function isGrowthEngineSubscriptionsFeaturePath(pathname) {
  const path = normalizeGrowthEnginePath(pathname)
  return (
    /(?:^|\/)subscriptions$/.test(path) ||
    /(?:^|\/)subscriptions\/oms-plans$/.test(path)
  )
}

export function isGrowthEngineProgressPath(pathname) {
  return /(?:^|\/)progress$/.test(normalizeGrowthEnginePath(pathname))
}

/** Food Safety home + setup / reports / section — kitchen job owns the fold. */
export function isGrowthEngineFoodSafetyFeaturePath(pathname) {
  return /(?:^|\/)food-safety(?:\/|$)/.test(normalizeGrowthEnginePath(pathname))
}

export function isGrowthEngineCataloguePath(pathname) {
  return /(?:^|\/)catalogue$/.test(normalizeGrowthEnginePath(pathname))
}

export function isGrowthEngineCatalogueProductGateFrom(from) {
  return CATALOGUE_PRODUCT_GATE_FROM.has(String(from || '').trim())
}

function splitPathAndSearch(pathname, search) {
  const raw = String(pathname || '')
  if (raw.includes('?') && (search == null || search === '')) {
    const [path, queryAndHash = ''] = raw.split('?')
    return { path, search: queryAndHash.split('#')[0] }
  }
  return { path: raw, search: String(search || '').replace(/^\?/, '') }
}

export function parseGrowthEngineCatalogueHandoff(search) {
  const raw = String(search || '')
  const query = raw.includes('?') ? raw.split('?')[1] : raw
  const params = new URLSearchParams(query.split('#')[0])
  return params.get(GROWTH_ENGINE_CATALOGUE_HANDOFF_PARAM)
}

export function isGrowthEngineCatalogueProductGate(pathname, search) {
  const split = splitPathAndSearch(pathname, search)
  if (!isGrowthEngineCataloguePath(split.path)) return false
  return isGrowthEngineCatalogueProductGateFrom(
    parseGrowthEngineCatalogueHandoff(split.search),
  )
}

/** Tag a catalogue URL so chrome hides the Home tip (recipes / par gate). */
export function appendGrowthEngineCatalogueProductGate(path, from) {
  if (!path || !isGrowthEngineCatalogueProductGateFrom(from))
    return path || null
  const [withoutHash, hashPart] = String(path).split('#')
  const hash = hashPart != null ? `#${hashPart}` : ''
  const [pathname, existing] = withoutHash.split('?')
  const params = new URLSearchParams(existing || '')
  params.set(GROWTH_ENGINE_CATALOGUE_HANDOFF_PARAM, from)
  return `${pathname}?${params.toString()}${hash}`
}

export function isGrowthEngineOneJobFeaturePath(pathname) {
  return (
    isGrowthEngineRecipesFeaturePath(pathname) ||
    isGrowthEngineStockFeaturePath(pathname) ||
    isGrowthEngineParFeaturePath(pathname) ||
    isGrowthEngineInvoicesFeaturePath(pathname) ||
    isGrowthEngineReceiptingFeaturePath(pathname) ||
    isGrowthEngineSubscriptionsFeaturePath(pathname) ||
    isGrowthEngineProgressPath(pathname) ||
    isGrowthEngineFoodSafetyFeaturePath(pathname)
  )
}

/**
 * Home chrome on these pages steals the first-try empty.
 * Same hide as checkout: do not fetch or render the primary card.
 * Catalogue: only the product-gate handoff, not a normal nav visit.
 */
export function shouldHidePrimaryNudgeOnOneJobFeaturePage(pathname, search) {
  return (
    isGrowthEngineOneJobFeaturePath(pathname) ||
    isGrowthEngineCatalogueProductGate(pathname, search)
  )
}

/** Checkout / success + first-try one-job feature pages. */
export function shouldHidePrimaryNudgeOnVenueChrome(pathname, search) {
  return (
    shouldHidePrimaryNudgeOnCheckoutFlow(pathname) ||
    shouldHidePrimaryNudgeOnOneJobFeaturePage(pathname, search)
  )
}

/** Collapse the old 🎉 / “this tool helps you” marketing when Guided Success is up. */
export function shouldHideFirstTryMarketingBanners(showGuidedEmpty) {
  return Boolean(showGuidedEmpty)
}

/**
 * Catalogues / shopping-list banner: hide on first-try only when they already
 * have products to cost. 0 products — keep it so the next step is honest.
 */
export function shouldKeepRecipesShoppingListBanner({
  showGuidedEmpty = false,
  hasShoppingListProducts = false,
} = {}) {
  if (!showGuidedEmpty) return true
  return !hasShoppingListProducts
}
